import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { designSystem } from '../../../design-system';

export const ServicesContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '100px 120px',
  overflow: 'visible',
  zIndex: 1,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url(/service_section_background.webp)',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '50%',
    filter: 'blur(30px)',
    zIndex: -1,
    opacity: 0.8,
  },

  '@media (max-width: 1600px)': {
    padding: '90px 100px',
  },

  '@media (max-width: 1440px)': {
    padding: '80px 80px',
  },

  '@media (max-width: 1024px)': {
    padding: '70px 60px',
  },

  '@media (max-width: 768px)': {
    padding: '60px 24px',
    minHeight: 'auto',
    
    '&::before': {
      backgroundSize: '90%',
      backgroundPosition: 'center 120%',
    },
  },
});

export const ServicesContent = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '1600px',
  display: 'flex',
  gap: '48px',
  alignItems: 'center',

  '@media (max-width: 1600px)': {
    gap: '40px',
  },

  '@media (max-width: 1440px)': {
    gap: '36px',
  },

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: '48px',
    alignItems: 'center',
  },

  '@media (max-width: 768px)': {
    width: '100%',
    margin: '0 auto',
    gap: '32px',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

export const DeviceMockup = styled(Box)({
  flexShrink: 0,
  width: '35%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '64px',

  '@media (max-width: 1600px)': {
    marginRight: '56px',
  },

  '@media (max-width: 1440px)': {
    marginRight: '48px',
  },

  '@media (max-width: 1200px)': {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginRight: '0',
  },
});

export const MonitorFrame = styled(Box)(({ hasMockup }) => ({
  backgroundColor: hasMockup ? 'transparent' : '#F3F4F6',
  borderRadius: hasMockup ? '0' : '12px',
  padding: hasMockup ? '0' : '4px',
  boxShadow: hasMockup ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  width: '600px',

  '@media (max-width: 1600px)': {
    width: '540px',
  },

  '@media (max-width: 1440px)': {
    width: '500px',
  },

  '@media (max-width: 1200px)': {
    width: '360px',
  },

  '@media (max-width: 768px)': {
    width: '100%',
    maxWidth: '320px',
  },

  '@media (max-width: 480px)': {
    maxWidth: '280px',
  },
}));

export const MonitorScreen = styled(Box)(({ gradient, hasMockup }) => ({
  backgroundColor: hasMockup ? 'transparent' : '#FFFFFF',
  borderRadius: hasMockup ? '0' : '8px',
  height: hasMockup ? 'auto' : '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',

  '@media (max-width: 1600px)': {
    height: hasMockup ? 'auto' : '360px',
  },

  '@media (max-width: 1440px)': {
    height: hasMockup ? 'auto' : '340px',
  },

  '@media (max-width: 1200px)': {
    height: hasMockup ? 'auto' : '240px',
  },

  '@media (max-width: 768px)': {
    height: hasMockup ? 'auto' : '200px',
  },

  '@media (max-width: 480px)': {
    height: hasMockup ? 'auto' : '180px',
  },
}));

export const MonitorImage = styled('img')(({ hasMockup }) => ({
  width: '100%',
  height: hasMockup ? 'auto' : '100%',
  objectFit: hasMockup ? 'contain' : 'cover',
  borderRadius: hasMockup ? '0' : '8px',
}));

export const CenterContent = styled(Box)({
  width: '35%',
  minWidth: 0,
  maxWidth: '35%',
  color: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'flex-start',
  justifyContent: 'center',
  textAlign: 'left',
  padding: '48px',
  paddingRight: '48px',
  position: 'relative',
  overflow: 'visible',
  minHeight: '600px',
  marginRight: '-32px',

  '@media (max-width: 1600px)': {
    padding: '40px',
    paddingRight: '40px',
    marginRight: '-28px',
  },

  '@media (max-width: 1440px)': {
    padding: '32px',
    paddingRight: '32px',
    marginRight: '-24px',
  },

  '@media (max-width: 1200px)': {
    width: '100%',
    padding: '32px',
    paddingRight: '0',
    justifyContent: 'flex-start',
    marginRight: '0',
  },

  '@media (max-width: 768px)': {
    padding: '24px',
    minHeight: 'auto',
    flex: 'none',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },

  '@media (max-width: 480px)': {
    padding: '20px',
  },
});

export const MainTitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '72px',
  fontWeight: 300,
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'baseline',
  gap: '12px',
  width: '100%',
  minWidth: 0,
  overflow: 'visible',
  position: 'relative',
  paddingRight: '0',

  '& > span': {
    flexShrink: 1,
    minWidth: 0,
  },

  '@media (max-width: 1600px)': {
    fontSize: '64px',
    gap: '10px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '56px',
    gap: '10px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '48px',
    gap: '8px',
  },

  '@media (max-width: 768px)': {
    fontSize: '40px',
    gap: '8px',
    alignSelf: 'center',
    paddingRight: '80px',
  },

  '@media (max-width: 480px)': {
    fontSize: '32px',
    gap: '6px',
    alignSelf: 'center',
    paddingRight: '70px',
  },
});

export const WorkSpaceText = styled('span')({
  fontSize: '28px',
  display: 'inline',
  flexShrink: 1,
  minWidth: 0,

  '@media (max-width: 1600px)': {
    fontSize: '24px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '20px',
  },

  '@media (max-width: 768px)': {
    fontSize: '18px',
  },

  '@media (max-width: 480px)': {
    fontSize: '16px',
  },
});

export const Subtitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '20px',
  fontWeight: 500,
  marginTop: '12px',

  '@media (max-width: 1440px)': {
    fontSize: '18px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '16px',
  },

  '@media (max-width: 768px)': {
    fontSize: '15px',
  },

  '@media (max-width: 480px)': {
    fontSize: '14px',
  },
});

export const StatusBadge = styled(Box)({
  position: 'absolute',
  top: '0',
  right: '0',
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '10px',
  fontWeight: 500,
  color: '#FFFFFF',
  backgroundColor: 'transparent',
  border: '1px solid #FFFFFF',
  padding: '4px 8px',
  borderRadius: '4px',
  letterSpacing: '0.3px',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  transform: 'translateY(0)',

  '@media (max-width: 1440px)': {
    fontSize: '10px',
    padding: '3px 7px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '9px',
    padding: '3px 6px',
  },

  '@media (max-width: 768px)': {
    fontSize: '9px',
    padding: '3px 6px',
    top: '-2px',
    right: '0',
  },

  '@media (max-width: 480px)': {
    fontSize: '8px',
    padding: '2px 5px',
    top: '-2px',
    right: '0',
  },
});

export const Divider = styled(Box)({
  width: '100%',
  height: '1px',
  borderTop: '1px dashed rgba(255, 255, 255, 0.4)',
  marginTop: '16px',
  marginBottom: '20px',

  '@media (max-width: 1200px)': {
    marginTop: '14px',
    marginBottom: '18px',
  },

  '@media (max-width: 768px)': {
    marginTop: '12px',
    marginBottom: '16px',
  },
});

// サービスの外部サイトへのリンク（自社サービスの被リンク用）。
// Description の直下に置く控えめなピル型。
export const ServiceLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '28px',
  padding: '12px 22px',
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  color: '#FFFFFF',
  textDecoration: 'none',
  border: '1px solid rgba(255, 255, 255, 0.7)',
  borderRadius: '999px',
  transition: 'background-color 0.2s ease, color 0.2s ease',

  '&:hover': {
    backgroundColor: '#FFFFFF',
    color: '#0B1A4D',
  },

  '@media (max-width: 1440px)': {
    marginTop: '24px',
    fontSize: '14px',
    padding: '10px 20px',
  },

  '@media (max-width: 768px)': {
    marginTop: '20px',
    fontSize: '13px',
    padding: '10px 18px',
  },
});

export const Description = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '18px',
  fontWeight: 400,
  marginTop: '0',
  lineHeight: 1.8,
  width: '100%',

  '@media (max-width: 1600px)': {
    fontSize: '17px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '16px',
  },

  '@media (max-width: 1200px)': {
    maxWidth: '100%',
    fontSize: '15px',
  },

  '@media (max-width: 768px)': {
    fontSize: '14px',
    maxWidth: '100%',
  },

  '@media (max-width: 480px)': {
    fontSize: '13px',
  },
});

export const NavIcon = styled('img')({
  position: 'absolute',
  top: 'calc(50% + 40px)',
  transform: 'translateY(-50%)',
  width: '80px',
  height: '80px',
  transition: 'all 0.4s ease',
  zIndex: 10,
  objectFit: 'contain',
  cursor: 'pointer',

  '&:hover': {
    opacity: 0.7,
    transform: 'translateY(-50%) scale(1.1)',
  },

  '@media (max-width: 1600px)': {
    width: '72px',
    height: '72px',
  },

  '@media (max-width: 1440px)': {
    width: '64px',
    height: '64px',
  },

  '@media (max-width: 1200px)': {
    width: '60px',
    height: '60px',
  },

  '@media (max-width: 768px)': {
    width: '48px',
    height: '48px',
    top: 'calc(50% + 30px)',
  },

  '@media (min-width: 1200px)': {
    display: 'none',
  },
});

export const LeftNavIcon = styled(NavIcon)({
  left: '0',
});

export const RightNavIcon = styled(NavIcon)({
  right: '0',
});

export const ServiceCard = styled(motion.div)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  alignItems: 'center',
});

export const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '48px',
  width: '100%',

  '@media (max-width: 1600px)': {
    marginBottom: '44px',
  },

  '@media (max-width: 1440px)': {
    marginBottom: '40px',
  },

  '@media (max-width: 1024px)': {
    marginBottom: '36px',
  },

  '@media (max-width: 768px)': {
    marginBottom: '32px',
  },
});

export const SectionTitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '24px',
  fontWeight: 500,
  color: '#FFFFFF',
  letterSpacing: '0.5px',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },

  '@media (max-width: 768px)': {
    fontSize: '18px',
  },
});

export const ServiceCounter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CounterText = styled(Typography)(({ active }) => ({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '24px',
  fontWeight: 500,
  color: '#FFFFFF',
  opacity: active ? 1 : 0.3,
  letterSpacing: '0.5px',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },

  '@media (max-width: 768px)': {
    fontSize: '18px',
  },
}));

export const CounterDivider = styled(Box)({
  width: '24px',
  height: '1px',
  backgroundColor: '#FFFFFF',
  transform: 'rotate(180deg)',
});

export const ServiceTabs = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '600px',
  overflow: 'visible',
});

export const ServiceTabsWrapper = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  padding: '32px 24px',
  borderRadius: '0',
  background: 'transparent',
  justifyContent: 'center',
  width: '30%',
  minWidth: 0,
  flexShrink: 0,
  zIndex: 1,
  overflow: 'visible',

  '@media (max-width: 1600px)': {
    padding: '28px 20px',
  },

  '@media (max-width: 1440px)': {
    padding: '28px 20px',
  },

  '@media (max-width: 1200px)': {
    width: '100%',
    maxWidth: '100%',
    alignItems: 'center',
    padding: '24px 20px',
    borderRadius: '0',
  },

  '@media (max-width: 768px)': {
    display: 'none',
  },
});

export const ServiceTab = styled(motion.div)(({ selected, gradient }) => ({
  position: 'relative',
  width: '140px',
  height: '140px',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  overflow: 'visible',
  zIndex: selected ? 10 : 1,

  '@media (max-width: 1600px)': {
    width: '130px',
    height: '130px',
  },

  '@media (max-width: 1440px)': {
    width: '120px',
    height: '120px',
  },

  '@media (max-width: 1200px)': {
    width: '100px',
    height: '100px',
  },
}));

export const ServiceTabRing = styled(motion.div)({
  position: 'absolute',
  inset: '-8px',
  borderRadius: '50%',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  zIndex: 0,
  pointerEvents: 'none',
  boxShadow: '0 0 20px rgba(255, 255, 255, 0.2), 0 0 40px rgba(255, 255, 255, 0.15)',
});

export const ServiceTabGlassOverlay = styled(Box)(({ selected }) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  backdropFilter: selected ? 'blur(2px)' : 'blur(4px)',
  WebkitBackdropFilter: selected ? 'blur(2px)' : 'blur(4px)',
  backgroundColor: selected ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.15)',
  transition: 'all 0.3s ease',
  opacity: selected ? 0.3 : 1,
  pointerEvents: 'none',
  zIndex: 2,
}));

export const ServiceIconContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  position: 'relative',
  padding: '8px',
  zIndex: 10,
});

export const ServiceTabImage = styled(motion.img)({
  width: '100%',
  height: '100%',
  maxWidth: '140px',
  maxHeight: '140px',
  objectFit: 'contain',
  display: 'block',

  '@media (max-width: 1600px)': {
    maxWidth: '130px',
    maxHeight: '130px',
  },

  '@media (max-width: 1440px)': {
    maxWidth: '120px',
    maxHeight: '120px',
  },

  '@media (max-width: 1200px)': {
    maxWidth: '100px',
    maxHeight: '100px',
  },

  '@media (max-width: 768px)': {
    maxWidth: '70px',
    maxHeight: '70px',
  },

  '@media (max-width: 480px)': {
    maxWidth: '60px',
    maxHeight: '60px',
  },
});

